import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  removeTokens,
  getCentreAccessToken,
  removeCentreTokens,
} from '@/utils/storage';
import { toast } from 'sonner';
import { loginPath } from '@/lib/returnTo';

// Host root (no /api/ prefix). Each API module prefixes its own path using V1 or V2.
const RAW_BASE = import.meta.env.VITE_API_BASE_URL || 'https://spa-htxu.onrender.com/';
// Exported so the SSE transport (src/api/aiStream.ts) targets the same host.
export const BASE_URL = RAW_BASE.endsWith('/') ? RAW_BASE : `${RAW_BASE}/`;

export const V1 = 'api/v1/';
export const V2 = 'api/v2/recovry/';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 100000,
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * A 401 means "session expired" only for a request that carried a session.
 * Signing in with a wrong password is also a 401, and treating it as expiry
 * reloaded the login page with a "Session expired" toast and empty fields,
 * so the person never saw "Invalid credentials".
 */
function isSessionRequest(config: { url?: string; headers?: Record<string, unknown> }): boolean {
  if (/\/auth\/(login|google|signup|register|token|forgot|reset|verify|otp)/.test(config.url || '')) {
    return false;
  }
  return !!config.headers?.Authorization;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config || {};
    if (error.response?.status === 401 && !originalRequest._retry && isSessionRequest(originalRequest)) {
      originalRequest._retry = true;
      try {
        const refresh = getRefreshToken();
        if (!refresh) throw new Error('No refresh token');
        const resp = await axios.post(`${BASE_URL}${V1}auth/token/refresh/`, { refresh });
        const newAccess = resp.data.access || resp.data.token || resp.data.access_token;
        setAccessToken(newAccess);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return axios(originalRequest);
      } catch {
        removeTokens();
        toast.error('Session expired. Please login again.');
        window.location.href = loginPath(window.location);
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

/* ------------------------------------------------------------------ */
/*  Centre-owner portal client                                        */
/*  Separate instance: attaches the centre token (not the customer    */
/*  token) and bounces to /partner/login on 401, so the two sessions  */
/*  never interfere.                                                  */
/* ------------------------------------------------------------------ */
export const centreApi = axios.create({
  baseURL: BASE_URL,
  timeout: 100000,
});

centreApi.interceptors.request.use(
  (config) => {
    const token = getCentreAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

centreApi.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      removeCentreTokens();
      if (!window.location.pathname.startsWith('/partner/login')) {
        toast.error('Session expired. Please log in again.');
        window.location.href = '/partner/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
